import { Button } from "@tellescope/design-system";
import {
  Checkbox,
  Combobox,
  type ComboboxOption,
  DateTimePicker,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { Plus } from "lucide-react";
import { type Dispatch, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddToJourneyPopoverProps {
  options: ComboboxOption[];
  schedulableOptions: ComboboxOption[];
  searchQuery: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
  isSearching?: boolean;
  onAdd: (journeyId: string) => void;
  onSchedule: (journeyId: string, addAt: Date) => void;
}

export const AddToJourneyPopover = ({
  options,
  schedulableOptions,
  searchQuery,
  setSearchQuery,
  isSearching,
  onAdd,
  onSchedule,
}: AddToJourneyPopoverProps) => {
  const { t } = useTranslation("contacts");

  const [open, setOpen] = useState(false);
  const [journey, setJourney] = useState<ComboboxOption>();
  const [isScheduled, setIsScheduled] = useState(false);
  const [addAt, setAddAt] = useState<Date>();

  const close = () => {
    setJourney(undefined);
    setIsScheduled(false);
    setAddAt(undefined);
    setOpen(false);
  };

  const submit = () => {
    if (!journey) return;
    if (isScheduled) {
      if (!addAt) return;
      onSchedule(journey.value, addAt);
    } else {
      onAdd(journey.value);
    }
    close();
  };

  const selectableOptions = isScheduled ? schedulableOptions : options;

  return (
    <Popover open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <PopoverTrigger asChild>
        <Button iconOnly variant="ghost" size="sm" color="muted" hoverLabel={t("Add to journey")}>
          <Plus />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex w-80 flex-col gap-3 p-3" align="end">
        <Combobox
          options={selectableOptions}
          value={journey}
          onChange={setJourney}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          customFilter={(_, loaded) => loaded}
          isLoading={isSearching}
          placeholder={t("Select a journey")}
          searchPlaceholder={t("Search journeys...")}
          emptyText={t("No journeys")}
        />

        <div className="flex items-center gap-2">
          <Checkbox
            id="schedule-journey"
            checked={isScheduled}
            onCheckedChange={(checked) => {
              setIsScheduled(checked === true);
              setAddAt(undefined);
            }}
          />
          <Label htmlFor="schedule-journey" className="text-xs font-normal">
            {t("Schedule for later")}
          </Label>
        </div>

        {isScheduled && (
          <DateTimePicker
            label={t("Add to journey at")}
            value={addAt}
            onChange={setAddAt}
            disabledDates={(date) => date < new Date()}
          />
        )}

        <Button size="sm" disabled={!journey || (isScheduled && !addAt)} onClick={submit}>
          {isScheduled ? t("Schedule") : t("Add to journey")}
        </Button>
      </PopoverContent>
    </Popover>
  );
};
